<template>
  <div class="tag-search-results__wrapper">
    <a-text-input
      v-model="searchPhrase"
      :max-length="$options.consts.QUICK_SEARCH_MAX_LENGTH_LIMITATION"
      search-icon
      placeholder="What are you looking for ?"
      class="tag-search-results__search-input"
      @search="search"
      @keyup.enter="search"
    />

    <a-tag-search-result
      :title="searchTitle"
      :tag-list="tagList"
      :page-count="pagesCount"
      :in-progress="inProgress"
      class="tag-search-results__results"
      @request-tags="getTagList"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex'

import { ROUTE, ROUTE_NAME } from 'enums/routes'
import { MIDDLEWARE } from 'enums/middleware'
import ATagSearchResult from '@/components/_sections/tag/TagSearchResult'
import {
  generateCanonicalUrlForStaticPage,
  checkIfNonCanonicalPath
} from '@/utils/helpers/canonical-links'
import { redirectHandler } from '@/utils/helpers/redirects/helpers'
import { QUICK_SEARCH_MAX_LENGTH_LIMITATION } from 'enums/quicksearch'
import { formatSearchPhrase } from '@/utils/helpers/formatSearchPhrase'

export const MIN_CHAR_COUNT_FOR_TAG_SEARCH = 2
const SEARCH_TAG_TAKE = 20
const TAG_LIST_MODEL = [
  {
    Name: '',
    Slug: ''
  }
]

export const skipMiddleware = [MIDDLEWARE.SEO_REDIRECT, MIDDLEWARE.PAGE_SEO]

const SEARCH_TAG_TITLE_BASE = 'Search Results for '

export default {
  name: 'TagSearchResultsPage',
  components: { ATagSearchResult },
  consts: {
    QUICK_SEARCH_MAX_LENGTH_LIMITATION
  },
  async asyncData(ctx) {
    const { path: currentPath, fullPath } = ctx.route
    const { searchPhrase } = ctx.route.query

    try {
      const canonicalUrl = generateCanonicalUrlForStaticPage(ctx, {
        staticPath: ROUTE.TAG_SEARCH_RESULTS
      })

      if (checkIfNonCanonicalPath(ctx, { currentPath, canonicalUrl })) {
        redirectHandler({ ctx, to: canonicalUrl })
        return
      }

      const dispatch = ctx.store.dispatch
      const applyFallback = ctx.app.$helper.applyFallback

      const isSearchAllowed =
        searchPhrase && searchPhrase.length >= MIN_CHAR_COUNT_FOR_TAG_SEARCH

      if (!isSearchAllowed) {
        redirectHandler({
          ctx,
          to: {
            name: ROUTE_NAME.TERMS
          }
        })
      }

      const { Items, TotalCount, PagesCount } = await dispatch(
        'globalSearch/requestTagsFullSearch',
        {
          queryParams: {
            searchPhrase: formatSearchPhrase(searchPhrase),
            take: SEARCH_TAG_TAKE,
            page: 0
          }
        }
      )
      return {
        tagList: applyFallback(Items, TAG_LIST_MODEL),
        key: ctx.app.$helper.guid(),
        totalCount: TotalCount,
        pagesCount: PagesCount,
        inProgress: false
      }
    } catch (err) {
      ctx.app.$errorHandler(err, ctx)
    } finally {
      await ctx.store.dispatch('router/removeRouteFromQueue', fullPath)
    }
  },
  data() {
    return {
      searchPhrase: '',
      tagList: [],
      totalCount: 0,
      pagesCount: 0,
      inProgress: false,
      key: null
    }
  },
  computed: {
    searchTitle() {
      return `${SEARCH_TAG_TITLE_BASE}"${this.queryParams}" (${this.totalCount})`
    },
    queryParams() {
      return this.$route.query.searchPhrase
    },
    isMinLengthValidationFailed() {
      return this.searchPhrase.length < MIN_CHAR_COUNT_FOR_TAG_SEARCH
    }
  },
  watch: {
    queryParams: {
      handler(newVal) {
        if (newVal && newVal.length >= MIN_CHAR_COUNT_FOR_TAG_SEARCH) {
          this.searchPhrase = newVal
          this.resetData()
          this.getTagList()
        }
      }
    }
  },
  methods: {
    ...mapActions({
      requestTagsFullSearch: 'globalSearch/requestTagsFullSearch'
    }),
    search() {
      if (this.isMinLengthValidationFailed) return

      this.updateQueryParam()
    },
    updateQueryParam() {
      this.$router.push({
        name: ROUTE_NAME.TAG_SEARCH_RESULTS,
        params: { skipMiddleware },
        query: {
          searchPhrase: this.searchPhrase
        }
      })
    },
    async getTagList({ take = SEARCH_TAG_TAKE, page = 0 } = {}) {
      this.inProgress = true
      try {
        const {
          Items,
          TotalCount,
          PagesCount
        } = await this.requestTagsFullSearch({
          queryParams: {
            take,
            page,
            searchPhrase: formatSearchPhrase(this.queryParams)
          }
        })

        this.tagList = [
          ...this.tagList,
          ...this.$helper.applyFallback(Items, TAG_LIST_MODEL)
        ]
        this.totalCount = TotalCount
        this.pagesCount = PagesCount
      } catch (err) {
        this.$errorHandler(err, this, {
          showErrorPage: true,
          showMessage: false
        })
      } finally {
        this.inProgress = false
      }
    },
    resetData() {
      this.tagList = []
      this.totalCount = 0
      this.pagesCount = 0
      this.inProgress = false
    }
  },
  beforeMount() {
    this.searchPhrase = this.queryParams
  }
}
</script>

<style lang="scss" scoped>
.tag-search-results__wrapper {
  .tag-search-results__search-input {
    display: block;
    width: 800px;
    max-width: 100%;
    margin: 40px auto 0;

    @include mobile {
      width: 100%;
      margin-top: 20px;
    }
  }

  .tag-search-results__results {
    margin-top: 30px;
  }
}
</style>
